import React from 'react';
import { Row, Col, Button, Card, Form, Spinner, Table, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { QuestionCircleFill } from 'react-bootstrap-icons';

const FolhaPagamentoTab = ({
  mesFolha,
  setMesFolha,
  anoFolha,
  setAnoFolha,
  dadosFolhaPagamento,
  isLoadingFolha,
  resumoFolha,
  isProcessingFolhaFinal,
  handleGerarOuVisualizarFolha,
  handleProcessarFolhaFinal,
  handleOpenDetalhesHoleriteModal, // Handler para abrir o modal de detalhes (definido no pai)
}) => {

  // Helper function to format currency (can be moved to a utility file if used elsewhere)
  const formatCurrency = (value) => {
    // Ensure value is a number before formatting
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return 'R$ 0,00';
    return numericValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const renderSalarioTooltip = (props) => (
    <Tooltip id="salario-base-tooltip" {...props}>
      Esta informação é resgatada do perfil de cada funcionário em Pessoal &gt;&gt; Colaboradores 'Salário'.
    </Tooltip>
  );

  const   renderLancamentosTooltip = (props) => (
    <Tooltip id="lancamentos-tooltip" {...props}>
      Este registro é realizado em Pessoal &gt;&gt; Colaboradores - Lançamentos.
    </Tooltip>
  );

  const renderPeriodoFuturoTooltip = (props) => (
    <Tooltip id="periodo-invalido-tooltip" {...props}>
      A folha de pagamento só pode ser gerada para meses anteriores ao atual.
    </Tooltip>
  );

  return (
    <Card>
      <Card.Header>
        <Row className="align-items-end">
          <Col md={3}>
            <Form.Group controlId="mesFolhaTab"> {/* Adicionado "Tab" para evitar conflito de ID */}
              <Form.Label>Mês:</Form.Label>
              <Form.Select value={mesFolha} onChange={(e) => setMesFolha(parseInt(e.target.value, 10))}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                  <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="anoFolhaTab"> {/* Adicionado "Tab" para evitar conflito de ID */}
              <Form.Label>Ano:</Form.Label>
              <Form.Control
                type="number"
                value={anoFolha}
                onChange={(e) => setAnoFolha(parseInt(e.target.value, 10))}
                min="2025"
                max={new Date().getFullYear() + 1}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            {(() => {
              const hoje = new Date();
              const mesAtual = hoje.getMonth() + 1;
              const anoAtual = hoje.getFullYear();
              // A folha não pode ser gerada se o ano for futuro, ou se o ano for o atual e o mês for futuro.
              const isPeriodoInvalido = anoFolha > anoAtual || (anoFolha === anoAtual && mesFolha >= mesAtual);

              const button = (
                <Button
                  variant="primary"
                  onClick={handleGerarOuVisualizarFolha}
                  disabled={isLoadingFolha || resumoFolha?.statusFolha === 'Paga' || isPeriodoInvalido}
                  className="w-100"
                  style={isPeriodoInvalido ? { pointerEvents: 'none' } : {}}
                >
                  {isLoadingFolha ? <Spinner as="span" animation="border" size="sm" /> :
                   (resumoFolha?.statusFolha === 'Processada' || resumoFolha?.statusFolha === 'Paga' ? 'Visualizar Folha' : 'Gerar Folha')
                  }
                </Button>
              );

              if (isPeriodoInvalido) return <OverlayTrigger placement="top" overlay={renderPeriodoFuturoTooltip}>{button}</OverlayTrigger>;
              return button;
            })()}
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {isLoadingFolha ? (
          <div className="text-center"><Spinner animation="border" /></div>
        ) : dadosFolhaPagamento.length > 0 ? (
          <>
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th className="d-none d-md-table-cell">Cargo</th>
                  <th className="text-end">
                    Salário Base (R$){' '}
                    <OverlayTrigger
                      placement="top"
                      overlay={renderSalarioTooltip}
                    >
                      <QuestionCircleFill size={12} style={{ cursor: 'pointer', verticalAlign: 'middle' }} />
                    </OverlayTrigger>
                  </th>
                  <th className="text-end d-none d-lg-table-cell">
                    Horas Extras (R$){' '}
                    <OverlayTrigger
                      placement="top"
                      overlay={renderLancamentosTooltip}
                    >
                      <QuestionCircleFill size={12} style={{ cursor: 'pointer', verticalAlign: 'middle' }} />
                    </OverlayTrigger>
                  </th>
                  <th className="text-end d-none d-lg-table-cell">
                    Outros Adicionais (R$){' '}
                    <OverlayTrigger
                      placement="top"
                      overlay={renderLancamentosTooltip}
                    >
                      <QuestionCircleFill size={12} style={{ cursor: 'pointer', verticalAlign: 'middle' }} />
                    </OverlayTrigger>
                  </th>
                  <th className="text-end d-none d-md-table-cell">INSS (R$)</th>
                  <th className="text-end d-none d-md-table-cell">IRRF (R$)</th>
                  <th className="text-end d-none d-md-table-cell">
                    Outros Descontos (R$){' '}
                    <OverlayTrigger
                      placement="top"
                      overlay={renderLancamentosTooltip}
                    >
                      <QuestionCircleFill size={12} style={{ cursor: 'pointer', verticalAlign: 'middle' }} />
                    </OverlayTrigger>
                  </th>
                  <th className="text-end">Valor Líquido (R$)</th>
                  <th className="d-none d-md-table-cell">Status</th>
                  <th className="d-none d-md-table-cell">Ações</th>
                </tr>
              </thead>
              <tbody>
                {dadosFolhaPagamento.map((item, index) => {
                  // Calcula os valores de proventos e descontos a partir dos lançamentos
                  const proventos = item.lancamentos?.filter(l => l.tipo_evento_historico === 'provento') || [];
                  const descontos = item.lancamentos?.filter(l => l.tipo_evento_historico === 'desconto') || [];

                  // Separa os valores por tipo de evento
                  const horasExtras = proventos.find(p => p.descricao_evento_historico.includes('Horas Extras'))?.valor_calculado || 0;
                  const adicionais = proventos
                    .filter(p => !p.descricao_evento_historico.includes('Salário Base') && !p.descricao_evento_historico.includes('Horas Extras') && !p.descricao_evento_historico.includes('Pagamento de Serviços (PJ)'))
                    .reduce((sum, p) => sum + p.valor_calculado, 0);

                  const descontoINSS = descontos.find(d => d.descricao_evento_historico.includes('INSS'))?.valor_calculado || 0;
                  const descontoIRRF = descontos.find(d => d.descricao_evento_historico.includes('IRRF'))?.valor_calculado || 0;
                  const outrosDescontos = descontos
                    .filter(d => !d.descricao_evento_historico.includes('INSS') && !d.descricao_evento_historico.includes('IRRF'))
                    .reduce((sum, d) => sum + d.valor_calculado, 0);

                  return (
                  <tr key={item.id_funcionario || `item-${index}`}>
                    <td>{item.nome}</td>
                    <td className="d-none d-md-table-cell">{item.cargo}</td>
                    <td className="text-end">{formatCurrency(item.salarioBase)}</td>
                    <td className="text-end d-none d-lg-table-cell">{formatCurrency(horasExtras)}</td>
                    <td className="text-end d-none d-lg-table-cell">{formatCurrency(adicionais)}</td>
                    <td className="text-end text-danger d-none d-md-table-cell">({formatCurrency(descontoINSS)})</td>
                    <td className="text-end text-danger d-none d-md-table-cell">({formatCurrency(descontoIRRF)})</td>
                    <td className="text-end text-danger d-none d-md-table-cell">({formatCurrency(outrosDescontos)})</td>
                    <td className="text-end fw-bold">{formatCurrency(item.valorLiquido)}</td>
                    <td className="d-none d-md-table-cell"><Badge bg={item.status === 'Paga' ? 'success' : item.status === 'Processada' || item.status === 'Liberado para Pagamento' ? 'warning' : 'secondary'}>{item.status}</Badge></td>
                    <td className="d-none d-md-table-cell"><Button variant="outline-primary" size="sm" onClick={() => handleOpenDetalhesHoleriteModal(item)}>Detalhes</Button></td>
                  </tr>
                )})}
              </tbody>
            </Table>
            <div className="mt-3 p-3 border rounded">
              <h5>Resumo da Folha ({`${mesFolha.toString().padStart(2, '0')}/${anoFolha}`})</h5>
              <p><strong>Total Bruto:</strong> {formatCurrency(resumoFolha.totalBruto)}</p>
              <p><strong>Total Descontos:</strong> {formatCurrency(resumoFolha.totalDescontos)}</p>
              <p><strong>Total Líquido a Pagar:</strong> {formatCurrency(resumoFolha.totalLiquido)}</p>
              <Button
                variant="success"
                className="me-2"
                onClick={handleProcessarFolhaFinal}
                disabled={isLoadingFolha || isProcessingFolhaFinal || resumoFolha?.statusFolha !== 'Processada'}>
                  {isProcessingFolhaFinal ? <Spinner as="span" animation="border" size="sm" /> : (resumoFolha?.statusFolha === 'Paga' ? 'Folha Paga' : 'Processar Folha')}
              </Button>
              <Button variant="info" disabled>Exportar p/ Contabilidade</Button>
            </div>
          </>
        ) : !isLoadingFolha && (
          <p className="text-center">Selecione o período e clique em "Gerar/Visualizar Folha" para ver os dados.</p>
        )}
      </Card.Body>
    </Card>
  );
};

export default FolhaPagamentoTab;